import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { toast } from 'sonner';
import { 
  Users, UserCog, Shield, Plus, Edit, Edit2, Trash2, Save, 
  Search, Loader2, CheckCircle, XCircle, Eye, EyeOff,
  DollarSign, GraduationCap, Brain, Briefcase, BarChart3,
  Key, Mail, Phone, ChevronDown, ChevronRight, Link2, Unlink
} from 'lucide-react';
import api from '../lib/api';
import { TODAS_PAGINAS, PERMISSOES_PADRAO, ACOES_POR_PAGINA, ACOES_LABELS } from '../lib/permissoes';

// Definição dos perfis de acesso
const PERFIS = {
  admin: {
    nome: 'Administrador',
    descricao: 'Acesso total a todas as funcionalidades',
    cor: 'bg-red-100 text-red-700',
    icon: Shield
  },
  professor: {
    nome: 'Professor',
    descricao: 'Acesso ao Pedagógico e DNA.IA',
    cor: 'bg-blue-100 text-blue-700',
    icon: GraduationCap
  },
  coordenacao: {
    nome: 'Coordenação',
    descricao: 'Acesso ao Pedagógico, DNA.IA e Relatórios',
    cor: 'bg-purple-100 text-purple-700',
    icon: UserCog
  },
  secretaria: {
    nome: 'Secretaria',
    descricao: 'Acesso básico ao Pedagógico',
    cor: 'bg-green-100 text-green-700',
    icon: Briefcase
  },
  financeiro: {
    nome: 'Financeiro',
    descricao: 'Acesso ao módulo Financeiro e Relatórios',
    cor: 'bg-yellow-100 text-yellow-700',
    icon: DollarSign
  },
  portaria: {
    nome: 'Portaria',
    descricao: 'Acesso ao DNA GO e DNA CONNECT',
    cor: 'bg-orange-100 text-orange-700',
    icon: Shield
  }
};

// Categorias para agrupar as páginas
const CATEGORIAS = [
  { id: 'dnacore', nome: 'DNA Core', icon: Brain, cor: 'text-indigo-600', bgCor: 'bg-indigo-50' },
  { id: 'financeiro', nome: 'Financeiro', icon: DollarSign, cor: 'text-green-600', bgCor: 'bg-green-50' },
  { id: 'pedagogico', nome: 'Pedagogico', icon: GraduationCap, cor: 'text-blue-600', bgCor: 'bg-blue-50' },
  { id: 'dna_ia', nome: 'DNA.IA', icon: Brain, cor: 'text-purple-600', bgCor: 'bg-purple-50' },
  { id: 'administrativo', nome: 'Administrativo', icon: Briefcase, cor: 'text-orange-600', bgCor: 'bg-orange-50' },
  { id: 'relatorios', nome: 'Relatorios', icon: BarChart3, cor: 'text-pink-600', bgCor: 'bg-pink-50' },
  { id: 'marketing', nome: 'Marketing', icon: Users, cor: 'text-cyan-600', bgCor: 'bg-cyan-50' },
  { id: 'dnago', nome: 'DNA GO', icon: Shield, cor: 'text-amber-600', bgCor: 'bg-amber-50' },
  { id: 'dnaconnect', nome: 'DNA CONNECT', icon: Users, cor: 'text-violet-600', bgCor: 'bg-violet-50' },
  { id: 'growth', nome: 'Growth Engine', icon: BarChart3, cor: 'text-emerald-600', bgCor: 'bg-emerald-50' },
  { id: 'fiscal', nome: 'Fiscal NFS-e', icon: Briefcase, cor: 'text-slate-600', bgCor: 'bg-slate-50' },
  { id: 'tributario', nome: 'Tributario', icon: DollarSign, cor: 'text-rose-600', bgCor: 'bg-rose-50' },
  { id: 'executivo', nome: 'Dashboard Executivo', icon: BarChart3, cor: 'text-sky-600', bgCor: 'bg-sky-50' },
  { id: 'estrategico', nome: 'Estrategico', icon: Brain, cor: 'text-fuchsia-600', bgCor: 'bg-fuchsia-50' },
  { id: 'calendario', nome: 'Calendario Escolar', icon: GraduationCap, cor: 'text-teal-600', bgCor: 'bg-teal-50' },
];

// Agrupar páginas por categoria
const getPaginasPorCategoria = (categoria) => {
  return Object.entries(TODAS_PAGINAS)
    .filter(([_, info]) => info.categoria === categoria)
    .map(([id, info]) => ({ id, ...info }));
};

const Usuarios = () => {
  const [loading, setLoading] = useState(true);
  const [usuarios, setUsuarios] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPerfil, setSelectedPerfil] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPermissoesModal, setShowPermissoesModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState([]);
  
  // Estado para perfis customizados
  const [perfisCustomizados, setPerfisCustomizados] = useState([]);
  const [showPerfilModal, setShowPerfilModal] = useState(false);
  const [editingPerfil, setEditingPerfil] = useState(null);
  const [novoPerfil, setNovoPerfil] = useState({
    nome: '',
    descricao: '',
    paginas: {}
  });
  const [activeTab, setActiveTab] = useState('usuarios');
  
  // Estado para edição de perfis do sistema
  const [showEditSistemaPerfil, setShowEditSistemaPerfil] = useState(false);
  const [editingSistemaPerfil, setEditingSistemaPerfil] = useState(null);
  const [sistemaPerfilPaginas, setSistemaPerfilPaginas] = useState({});
  
  // Estado para reset de senha
  const [resetPasswordData, setResetPasswordData] = useState({
    customPassword: '',
    sendEmail: true
  });
  
  // Estado para seleção em lote
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showBulkResetModal, setShowBulkResetModal] = useState(false);
  const [bulkPassword, setBulkPassword] = useState('');
  const [bulkSendEmail, setBulkSendEmail] = useState(true);
  const [bulkResetResults, setBulkResetResults] = useState(null);
  
  const [novoUsuario, setNovoUsuario] = useState({
    name: '',
    email: '',
    password: '',
    role: 'professor',
    active: true
  });
  
  // Estado para edição de usuário
  const [editUsuario, setEditUsuario] = useState({
    id: '',
    name: '',
    email: '',
    role: 'professor',
    active: true
  });
  
  // Estado para permissoes granulares por pagina e acoes
  const [permissoesEdit, setPermissoesEdit] = useState({ paginas: {}, acoes: {} });

  useEffect(() => {
    loadUsuarios();
    loadPerfisCustomizados();
  }, []);
  
  const loadPerfisCustomizados = async () => {
    try {
      const res = await api.get('/perfis');
      setPerfisCustomizados(res.data || []);
    } catch (error) {
      console.error('Erro ao carregar perfis:', error);
      setPerfisCustomizados([]);
    }
  };
  
  const handleSavePerfil = async () => {
    if (!novoPerfil.nome) {
      toast.error('Informe o nome do perfil');
      return;
    }
    
    setSaving(true);
    try {
      if (editingPerfil) {
        const response = await api.put(`/perfis/${editingPerfil.id}`, novoPerfil);
        const usuariosAtualizados = response.data?.usuarios_atualizados || 0;
        toast.success(
          `Perfil atualizado! ${usuariosAtualizados > 0 ? `${usuariosAtualizados} usuário(s) tiveram suas permissões sincronizadas.` : ''}`
        );
      } else {
        await api.post('/perfis', novoPerfil);
        toast.success('Perfil criado!');
      }
      setShowPerfilModal(false);
      setEditingPerfil(null);
      setNovoPerfil({ nome: '', descricao: '', paginas: {} });
      loadPerfisCustomizados();
    } catch (error) {
      toast.error('Erro ao salvar perfil');
    } finally {
      setSaving(false);
    }
  };
  
  const handleDeletePerfil = async (perfilId) => {
    if (!window.confirm('Tem certeza que deseja excluir este perfil?')) return;
    
    try {
      await api.delete(`/perfis/${perfilId}`);
      toast.success('Perfil excluído!');
      loadPerfisCustomizados();
    } catch (error) {
      toast.error('Erro ao excluir perfil');
    }
  };
  
  const handleEditPerfil = (perfil) => {
    setEditingPerfil(perfil);
    setNovoPerfil({
      nome: perfil.nome,
      descricao: perfil.descricao || '',
      paginas: perfil.paginas || {}
    });
    setShowPerfilModal(true);
  };
  
  const handleApplyPerfil = async (usuario, perfilId) => {
    const perfil = perfisCustomizados.find(p => p.id === perfilId);
    if (!perfil) return;
    
    try {
      await api.put(`/users/${usuario.id}/permissoes`, {
        paginas: perfil.paginas
      });
      toast.success(`Perfil "${perfil.nome}" aplicado a ${usuario.name}`);
      loadUsuarios();
    } catch (error) {
      toast.error('Erro ao aplicar perfil');
    }
  };

  const loadUsuarios = async () => {
    setLoading(true);
    try {
      const res = await api.get('/users');
      setUsuarios(res.data || []);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      setUsuarios([
        { id: 'admin-001', name: 'Administrador', email: 'financeiro@novodna.com.br', role: 'admin', active: true },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!novoUsuario.name || !novoUsuario.email || !novoUsuario.password) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    
    setSaving(true);
    try {
      let permissoes;
      let roleToSave = novoUsuario.role;
      
      // Verificar se é um perfil customizado
      if (novoUsuario.role.startsWith('custom_')) {
        const perfilId = novoUsuario.role.replace('custom_', '');
        const perfilCustomizado = perfisCustomizados.find(p => p.id === perfilId);
        if (perfilCustomizado) {
          permissoes = { paginas: perfilCustomizado.paginas || {} };
          roleToSave = 'custom'; // Role genérica para perfis customizados
        }
      } else {
        // Usar permissões padrão do perfil do sistema
        permissoes = PERMISSOES_PADRAO[novoUsuario.role] || PERMISSOES_PADRAO.professor;
      }
      
      await api.post('/users', {
        ...novoUsuario,
        role: roleToSave,
        custom_profile_id: novoUsuario.role.startsWith('custom_') ? novoUsuario.role.replace('custom_', '') : null,
        permissoes: permissoes
      });
      toast.success('Usuário criado com sucesso!');
      setShowModal(false);
      setNovoUsuario({ name: '', email: '', password: '', role: 'professor', active: true });
      loadUsuarios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar usuário');
    } finally {
      setSaving(false);
    }
  };

  const handleEditPermissoes = (user) => {
    setSelectedUser(user);
    if (user.permissoes && user.permissoes.paginas) {
      setPermissoesEdit({
        paginas: user.permissoes.paginas || {},
        acoes: user.permissoes.acoes || {}
      });
    } else {
      const permissoesPadrao = PERMISSOES_PADRAO[user.role] || PERMISSOES_PADRAO.professor;
      const acoesPadrao = {};
      Object.entries(permissoesPadrao.paginas || {}).forEach(([pageId, allowed]) => {
        if (allowed && ACOES_POR_PAGINA[pageId]) {
          const cat = TODAS_PAGINAS[pageId]?.categoria;
          ACOES_POR_PAGINA[pageId].forEach(acao => {
            acoesPadrao[`${cat}.${pageId}.${acao}`] = true;
          });
        }
      });
      setPermissoesEdit({ ...permissoesPadrao, acoes: acoesPadrao });
    }
    setShowPermissoesModal(true);
  };

  const handleSavePermissoes = async () => {
    if (!selectedUser) return;
    
    setSaving(true);
    try {
      await api.put(`/users/${selectedUser.id}/permissoes`, {
        permissoes: permissoesEdit
      });
      toast.success('Permissões atualizadas com sucesso!');
      setShowPermissoesModal(false);
      loadUsuarios();
    } catch (error) {
      toast.error('Erro ao atualizar permissões');
    } finally {
      setSaving(false);
    }
  };

  // Abrir modal de edição de dados básicos
  const handleEditUser = (user) => {
    // Se o usuário tem um perfil customizado, montar o ID completo
    let roleValue = user.role;
    if (user.role === 'custom' && user.custom_profile_id) {
      roleValue = `custom_${user.custom_profile_id}`;
    }
    
    setEditUsuario({
      id: user.id,
      name: user.name,
      email: user.email,
      role: roleValue,
      active: user.active !== false
    });
    setShowEditModal(true);
  };

  // Salvar edição de dados básicos
  const handleSaveEdit = async () => {
    if (!editUsuario.name || !editUsuario.email) {
      toast.error('Preencha nome e email');
      return;
    }
    
    setSaving(true);
    try {
      let roleToSave = editUsuario.role;
      let customProfileId = null;
      let permissoes = null;
      
      // Verificar se é um perfil customizado
      if (editUsuario.role.startsWith('custom_')) {
        const perfilId = editUsuario.role.replace('custom_', '');
        const perfilCustomizado = perfisCustomizados.find(p => p.id === perfilId);
        if (perfilCustomizado) {
          permissoes = { paginas: perfilCustomizado.paginas || {} };
          customProfileId = perfilId;
          roleToSave = 'custom';
        }
      }
      
      const updateData = {
        name: editUsuario.name,
        email: editUsuario.email,
        role: roleToSave,
        active: editUsuario.active,
        custom_profile_id: customProfileId
      };
      
      // Se mudou para um perfil customizado, também atualizar as permissões
      if (permissoes) {
        updateData.permissoes = permissoes;
      }
      
      await api.put(`/users/${editUsuario.id}`, updateData);
      toast.success('Usuário atualizado com sucesso!');
      setShowEditModal(false);
      loadUsuarios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar usuário');
    } finally {
      setSaving(false);
    }
  };

  const togglePagina = (paginaId) => {
    const novoValor = !permissoesEdit.paginas?.[paginaId];
    const cat = TODAS_PAGINAS[paginaId]?.categoria;
    setPermissoesEdit(prev => {
      const novasAcoes = { ...(prev.acoes || {}) };
      if (ACOES_POR_PAGINA[paginaId]) {
        ACOES_POR_PAGINA[paginaId].forEach(acao => {
          novasAcoes[`${cat}.${paginaId}.${acao}`] = novoValor;
        });
      }
      return {
        ...prev,
        paginas: { ...prev.paginas, [paginaId]: novoValor },
        acoes: novasAcoes
      };
    });
  };

  const toggleAcao = (paginaId, acao) => {
    const cat = TODAS_PAGINAS[paginaId]?.categoria;
    const key = `${cat}.${paginaId}.${acao}`;
    setPermissoesEdit(prev => ({
      ...prev,
      acoes: { ...(prev.acoes || {}), [key]: !(prev.acoes?.[key]) }
    }));
  };

  const toggleCategoria = (categoriaId) => {
    const paginasDaCategoria = getPaginasPorCategoria(categoriaId);
    const todasMarcadas = paginasDaCategoria.every(p => permissoesEdit.paginas?.[p.id]);
    setPermissoesEdit(prev => {
      const novasPaginas = { ...prev.paginas };
      const novasAcoes = { ...(prev.acoes || {}) };
      paginasDaCategoria.forEach(p => {
        novasPaginas[p.id] = !todasMarcadas;
        if (ACOES_POR_PAGINA[p.id]) {
          ACOES_POR_PAGINA[p.id].forEach(acao => {
            novasAcoes[`${categoriaId}.${p.id}.${acao}`] = !todasMarcadas;
          });
        }
      });
      return { ...prev, paginas: novasPaginas, acoes: novasAcoes };
    });
  };

  const toggleExpandCategoria = (categoriaId) => {
    setExpandedCategories(prev => 
      prev.includes(categoriaId) 
        ? prev.filter(id => id !== categoriaId)
        : [...prev, categoriaId]
    );
  };

  const marcarTodas = () => {
    const todasPaginas = {};
    const todasAcoes = {};
    Object.keys(TODAS_PAGINAS).forEach(id => {
      todasPaginas[id] = true;
      const cat = TODAS_PAGINAS[id]?.categoria;
      if (ACOES_POR_PAGINA[id]) {
        ACOES_POR_PAGINA[id].forEach(acao => {
          todasAcoes[`${cat}.${id}.${acao}`] = true;
        });
      }
    });
    setPermissoesEdit({ paginas: todasPaginas, acoes: todasAcoes });
  };

  const desmarcarTodas = () => {
    const todasPaginas = {};
    const todasAcoes = {};
    Object.keys(TODAS_PAGINAS).forEach(id => {
      todasPaginas[id] = false;
      const cat = TODAS_PAGINAS[id]?.categoria;
      if (ACOES_POR_PAGINA[id]) {
        ACOES_POR_PAGINA[id].forEach(acao => {
          todasAcoes[`${cat}.${id}.${acao}`] = false;
        });
      }
    });
    setPermissoesEdit({ paginas: todasPaginas, acoes: todasAcoes });
  };

  // Funções para editar perfis do sistema
  const handleEditSistemaPerfil = async (perfilKey) => {
    try {
      // Buscar configuração salva deste perfil do sistema no banco
      const response = await api.get(`/system-profiles/${perfilKey}`);
      const perfilConfig = response.data;
      
      setEditingSistemaPerfil(perfilKey);
      setSistemaPerfilPaginas(perfilConfig?.paginas || {});
      setShowEditSistemaPerfil(true);
    } catch (error) {
      // Se não existe, usar config vazia
      setEditingSistemaPerfil(perfilKey);
      setSistemaPerfilPaginas({});
      setShowEditSistemaPerfil(true);
    }
  };

  const handleSaveSistemaPerfil = async () => {
    if (!editingSistemaPerfil) return;
    
    setSaving(true);
    try {
      const response = await api.put(`/system-profiles/${editingSistemaPerfil}`, {
        paginas: sistemaPerfilPaginas
      });
      
      const usuariosAtualizados = response.data?.usuarios_atualizados || 0;
      toast.success(
        `Permissões do perfil "${PERFIS[editingSistemaPerfil].nome}" atualizadas! ${usuariosAtualizados} usuário(s) tiveram suas permissões sincronizadas.`
      );
      setShowEditSistemaPerfil(false);
      setEditingSistemaPerfil(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar permissões do perfil');
    } finally {
      setSaving(false);
    }
  };

  const toggleSistemaPaginaPerfil = (paginaId) => {
    setSistemaPerfilPaginas(prev => ({
      ...prev,
      [paginaId]: !prev[paginaId]
    }));
  };

  const toggleSistemaCategoriaPerfil = (categoriaId) => {
    const paginasDaCategoria = getPaginasPorCategoria(categoriaId);
    const todasMarcadas = paginasDaCategoria.every(p => sistemaPerfilPaginas[p.id]);
    
    setSistemaPerfilPaginas(prev => {
      const novasPaginas = { ...prev };
      paginasDaCategoria.forEach(p => {
        novasPaginas[p.id] = !todasMarcadas;
      });
      return novasPaginas;
    });
  };

  const contarPermissoes = () => {
    if (!permissoesEdit.paginas) return { ativas: 0, total: Object.keys(TODAS_PAGINAS).length, ativasAcoes: 0, totalAcoes: 0 };
    const ativas = Object.values(permissoesEdit.paginas).filter(v => v === true).length;
    const totalAcoes = Object.values(ACOES_POR_PAGINA).flat().length;
    const ativasAcoes = Object.values(permissoesEdit.acoes || {}).filter(v => v === true).length;
    return { ativas, total: Object.keys(TODAS_PAGINAS).length, ativasAcoes, totalAcoes };
  };

  const handleToggleActive = async (user) => {
    try {
      await api.put(`/users/${user.id}`, {
        active: !user.active
      });
      toast.success(user.active ? 'Usuário desativado' : 'Usuário ativado');
      loadUsuarios();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm('Tem certeza que deseja excluir este usuário?')) return;
    
    try {
      await api.delete(`/users/${userId}`);
      toast.success('Usuário excluído com sucesso!');
      loadUsuarios();
    } catch (error) {
      toast.error('Erro ao excluir usuário');
    }
  };

  const openResetPasswordModal = (user) => {
    setSelectedUser(user);
    setResetPasswordData({ customPassword: '', sendEmail: true });
    setShowResetPasswordModal(true);
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;
    
    setSaving(true);
    try {
      const params = new URLSearchParams();
      params.append('send_email_flag', resetPasswordData.sendEmail);
      if (resetPasswordData.customPassword) {
        params.append('custom_password', resetPasswordData.customPassword);
      }
      
      const response = await api.post(`/users/${selectedUser.id}/reset-password?${params.toString()}`);
      
      toast.success(
        <div>
          <p>Senha alterada com sucesso!</p>
          <p className="text-sm mt-1">Email: {response.data.email}</p>
          <p className="text-sm">Nova Senha: <code className="bg-gray-100 px-2 py-0.5 rounded">{response.data.password}</code></p>
          {resetPasswordData.sendEmail && response.data.email_sent && (
            <p className="text-sm text-green-600 mt-1">✓ Email enviado com sucesso</p>
          )}
          {resetPasswordData.sendEmail && !response.data.email_sent && (
            <p className="text-sm text-yellow-600 mt-1">⚠ Falha ao enviar email</p>
          )}
        </div>,
        { duration: 15000 }
      );
      setShowResetPasswordModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao resetar senha');
    } finally {
      setSaving(false);
    }
  };

  // Funções para seleção em lote
  const toggleSelectUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedUsers.length === usuariosFiltrados.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(usuariosFiltrados.map(u => u.id));
    }
  };

  const handleBulkResetPassword = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Selecione pelo menos um usuário');
      return;
    }
    if (bulkPassword.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }
    
    setSaving(true);
    try {
      const response = await api.post('/users/bulk-reset-password', {
        user_ids: selectedUsers,
        custom_password: bulkPassword,
        send_email: bulkSendEmail
      });
      
      setBulkResetResults(response.data);
      
      const successCount = response.data.results.filter(r => r.success).length;
      const emailCount = response.data.results.filter(r => r.email_sent).length;
      
      toast.success(
        <div>
          <p className="font-semibold">Senhas alteradas com sucesso!</p>
          <p className="text-sm">{successCount} usuários atualizados</p>
          {bulkSendEmail && <p className="text-sm">{emailCount} emails enviados</p>}
          <p className="text-sm mt-2">Nova senha: <code className="bg-gray-100 px-2 py-0.5 rounded">{response.data.password}</code></p>
        </div>,
        { duration: 15000 }
      );
      
      setSelectedUsers([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao resetar senhas em lote');
    } finally {
      setSaving(false);
    }
  };

  const usuariosFiltrados = usuarios.filter(u => {
    if (searchTerm && !u.name?.toLowerCase().includes(searchTerm.toLowerCase()) && 
        !u.email?.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    if (selectedPerfil !== 'all' && u.role !== selectedPerfil) {
      return false;
    }
    return true;
  });

  const stats = {
    total: usuarios.length,
    ativos: usuarios.filter(u => u.active !== false).length,
    porPerfil: Object.keys(PERFIS).reduce((acc, perfil) => {
      acc[perfil] = usuarios.filter(u => u.role === perfil).length;
      return acc;
    }, {})
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-r from-orange-600 to-amber-600 rounded-xl">
            <UserCog className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Usuários e Permissões</h1>
            <p className="text-muted-foreground">Gerencie os acessos ao sistema DNACore</p>
          </div>
        </div>
        <div className="flex gap-2">
          {activeTab === 'usuarios' && (
            <Button 
              onClick={() => {
                if (selectedUsers.length === 0) {
                  toast.info('Selecione os usuários na tabela para alterar as senhas em lote');
                  return;
                }
                setShowBulkResetModal(true);
              }} 
              variant="outline"
              className={`${selectedUsers.length > 0 
                ? 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100' 
                : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'}`}
            >
              <Key className="w-4 h-4 mr-2" />
              Alterar Senha em Lote {selectedUsers.length > 0 && `(${selectedUsers.length})`}
            </Button>
          )}
          {activeTab === 'perfis' && (
            <Button onClick={() => { setEditingPerfil(null); setNovoPerfil({ nome: '', descricao: '', paginas: {} }); setShowPerfilModal(true); }} variant="outline">
              <Plus className="w-4 h-4 mr-2" />
              Novo Perfil
            </Button>
          )}
          <Button onClick={() => setShowModal(true)} data-testid="novo-usuario-btn">
            <Plus className="w-4 h-4 mr-2" />
            Novo Usuário
          </Button>
        </div>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="usuarios">Usuários</TabsTrigger>
          <TabsTrigger value="perfis">Perfis de Acesso</TabsTrigger>
        </TabsList>
        
        <TabsContent value="usuarios" className="space-y-6">

      {/* Estatísticas */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-xs text-muted-foreground">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        {Object.entries(PERFIS).map(([key, perfil]) => {
          const Icon = perfil.icon;
          return (
            <Card key={key}>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <Icon className={`w-6 h-6 ${perfil.cor.split(' ')[1]}`} />
                  <div>
                    <p className="text-xl font-bold">{stats.porPerfil[key] || 0}</p>
                    <p className="text-xs text-muted-foreground">{perfil.nome}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2 flex-1 min-w-[200px]">
              <Search className="w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nome ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="max-w-sm"
              />
            </div>
            
            <Select value={selectedPerfil} onValueChange={setSelectedPerfil}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filtrar por perfil" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os Perfis</SelectItem>
                {Object.entries(PERFIS).map(([key, perfil]) => (
                  <SelectItem key={key} value={key}>{perfil.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tabela de Usuários */}
      <Card>
        <CardHeader>
          <CardTitle>Usuários Cadastrados</CardTitle>
          <CardDescription>
            {usuariosFiltrados.length} usuário(s) encontrado(s)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox 
                      checked={selectedUsers.length === usuariosFiltrados.length && usuariosFiltrados.length > 0}
                      onCheckedChange={toggleSelectAll}
                    />
                  </TableHead>
                  <TableHead>Usuário</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Funcionário</TableHead>
                  <TableHead>Perfil</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-center">Permissões</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {usuariosFiltrados.map(user => {
                  // Verificar se é perfil customizado
                  const isCustomProfile = user.role === 'custom' && user.custom_profile_id;
                  const customProfile = isCustomProfile ? perfisCustomizados.find(p => p.id === user.custom_profile_id) : null;
                  const perfil = isCustomProfile ? null : (PERFIS[user.role] || PERFIS.professor);
                  const Icon = perfil?.icon || Shield;
                  const corClass = perfil?.cor || 'bg-indigo-100 text-indigo-700';
                  const perfilNome = customProfile?.nome || perfil?.nome || 'Customizado';
                  const isSelected = selectedUsers.includes(user.id);
                  
                  return (
                    <TableRow key={user.id} className={isSelected ? 'bg-orange-50' : ''}>
                      <TableCell>
                        <Checkbox 
                          checked={isSelected}
                          onCheckedChange={() => toggleSelectUser(user.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-full ${corClass.split(' ')[0]}`}>
                            <Icon className={`w-4 h-4 ${corClass.split(' ')[1]}`} />
                          </div>
                          <span className="font-medium">{user.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{user.email}</TableCell>
                      <TableCell>
                        {user.linked_employee ? (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            <Link2 className="w-3 h-3 mr-1" />
                            {user.linked_employee.name}
                          </Badge>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={corClass}>{perfilNome}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={user.active !== false ? 'default' : 'secondary'}>
                          {user.active !== false ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-1">
                          {CATEGORIAS.map(categoria => {
                            const permissoes = user.permissoes || PERMISSOES_PADRAO[user.role];
                            const paginasDaCategoria = getPaginasPorCategoria(categoria.id);
                            const paginasComAcesso = paginasDaCategoria.filter(p => permissoes?.paginas?.[p.id]);
                            const temAcesso = paginasComAcesso.length > 0;
                            const CatIcon = categoria.icon;
                            return (
                              <div 
                                key={categoria.id}
                                className={`p-1 rounded ${temAcesso ? 'bg-green-100' : 'bg-gray-100'}`}
                                title={`${categoria.nome}: ${paginasComAcesso.length}/${paginasDaCategoria.length} páginas`}
                              >
                                <CatIcon className={`w-3 h-3 ${temAcesso ? categoria.cor : 'text-gray-400'}`} />
                              </div>
                            );
                          })}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleEditUser(user)}
                            title="Editar dados"
                          >
                            <Edit className="w-4 h-4 text-blue-600" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleEditPermissoes(user)}
                            title="Editar permissões"
                          >
                            <Shield className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => openResetPasswordModal(user)}
                            title="Alterar senha"
                            className="text-orange-600 hover:text-orange-700"
                          >
                            <Key className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleToggleActive(user)}
                            title={user.active !== false ? 'Desativar' : 'Ativar'}
                          >
                            {user.active !== false ? (
                              <EyeOff className="w-4 h-4 text-orange-600" />
                            ) : (
                              <Eye className="w-4 h-4 text-green-600" />
                            )}
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDeleteUser(user.id)}
                            title="Excluir"
                            disabled={user.role === 'admin'}
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      </TabsContent>
      
      {/* Aba de Perfis */}
      <TabsContent value="perfis" className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Perfis de Acesso Customizados
            </CardTitle>
            <CardDescription>
              Crie perfis personalizados para facilitar a atribuição de permissões
            </CardDescription>
          </CardHeader>
          <CardContent>
            {perfisCustomizados.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Shield className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p>Nenhum perfil customizado criado</p>
                <p className="text-sm mt-1">Clique em "Novo Perfil" para criar</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {perfisCustomizados.map(perfil => (
                  <Card key={perfil.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="font-semibold text-lg">{perfil.nome}</h3>
                          {perfil.descricao && (
                            <p className="text-sm text-muted-foreground">{perfil.descricao}</p>
                          )}
                        </div>
                        <Badge variant="outline">
                          {Object.keys(perfil.paginas || {}).length} páginas
                        </Badge>
                      </div>
                      <div className="flex gap-2 mt-4">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleEditPerfil(perfil)}
                          className="flex-1"
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Editar
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleDeletePerfil(perfil.id)}
                          className="text-destructive hover:bg-destructive/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Perfis Padrão do Sistema */}
        <Card>
          <CardHeader>
            <CardTitle>Perfis Padrão do Sistema</CardTitle>
            <CardDescription>
              Clique em um perfil para personalizar suas permissões
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(PERFIS).map(([key, perfil]) => {
                const Icon = perfil.icon;
                const bgColor = perfil.cor.split(' ')[0];
                const textColor = perfil.cor.split(' ')[1];
                return (
                  <div 
                    key={key} 
                    className={`p-4 rounded-lg border bg-white hover:shadow-md transition-shadow cursor-pointer group`}
                    onClick={() => handleEditSistemaPerfil(key)}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`p-2 rounded-full ${bgColor}`}>
                        <Icon className={`w-5 h-5 ${textColor}`} />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold">{perfil.nome}</p>
                        <p className="text-xs text-muted-foreground">{perfil.descricao}</p>
                      </div>
                      <Edit2 className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="outline">Perfil do Sistema</Badge>
                      <span className="text-xs text-muted-foreground">Clique para editar</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </TabsContent>
      </Tabs>

      {/* Modal Novo Usuário */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-primary" />
              Novo Usuário
            </DialogTitle>
            <DialogDescription>
              Cadastre um novo usuário no sistema
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nome Completo *</Label>
              <Input 
                placeholder="Nome do usuário"
                value={novoUsuario.name}
                onChange={(e) => setNovoUsuario(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input 
                type="email"
                placeholder="email@escola.com.br"
                value={novoUsuario.email}
                onChange={(e) => setNovoUsuario(prev => ({ ...prev, email: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Senha Inicial *</Label>
              <Input 
                type="password"
                placeholder="Mínimo 6 caracteres"
                value={novoUsuario.password}
                onChange={(e) => setNovoUsuario(prev => ({ ...prev, password: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Perfil de Acesso *</Label>
              <Select 
                value={novoUsuario.role} 
                onValueChange={(value) => setNovoUsuario(prev => ({ ...prev, role: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {/* Perfis Padrão do Sistema */}
                  {Object.entries(PERFIS).map(([key, perfil]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        <perfil.icon className="w-4 h-4" />
                        {perfil.nome}
                      </div>
                    </SelectItem>
                  ))}
                  {/* Perfis Customizados */}
                  {perfisCustomizados.length > 0 && (
                    <>
                      <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground border-t mt-1 pt-2">
                        Perfis Customizados
                      </div>
                      {perfisCustomizados.map((perfil) => (
                        <SelectItem key={`custom_${perfil.id}`} value={`custom_${perfil.id}`}>
                          <div className="flex items-center gap-2">
                            <Shield className="w-4 h-4 text-indigo-500" />
                            {perfil.nome}
                          </div>
                        </SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {PERFIS[novoUsuario.role]?.descricao || 
                  perfisCustomizados.find(p => `custom_${p.id}` === novoUsuario.role)?.descricao ||
                  'Perfil customizado'}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateUser} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Criar Usuário
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Editar Usuário */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-primary" />
              Editar Usuário
            </DialogTitle>
            <DialogDescription>
              Altere os dados do usuário
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nome Completo *</Label>
              <Input 
                placeholder="Nome do usuário"
                value={editUsuario.name}
                onChange={(e) => setEditUsuario(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input 
                type="email"
                placeholder="email@escola.com.br"
                value={editUsuario.email}
                onChange={(e) => setEditUsuario(prev => ({ ...prev, email: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Perfil de Acesso *</Label>
              <Select 
                value={editUsuario.role} 
                onValueChange={(value) => setEditUsuario(prev => ({ ...prev, role: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {/* Perfis Padrão do Sistema */}
                  {Object.entries(PERFIS).map(([key, perfil]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        <perfil.icon className="w-4 h-4" />
                        {perfil.nome}
                      </div>
                    </SelectItem>
                  ))}
                  {/* Perfis Customizados */}
                  {perfisCustomizados.length > 0 && (
                    <>
                      <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground border-t mt-1 pt-2">
                        Perfis Customizados
                      </div>
                      {perfisCustomizados.map((perfil) => (
                        <SelectItem key={`custom_${perfil.id}`} value={`custom_${perfil.id}`}>
                          <div className="flex items-center gap-2">
                            <Shield className="w-4 h-4 text-indigo-500" />
                            {perfil.nome}
                          </div>
                        </SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {PERFIS[editUsuario.role]?.descricao || 
                  perfisCustomizados.find(p => `custom_${p.id}` === editUsuario.role)?.descricao ||
                  'Perfil customizado'}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={editUsuario.active}
                onCheckedChange={(checked) => setEditUsuario(prev => ({ ...prev, active: checked }))}
              />
              <Label>Usuário Ativo</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Editar Permissões - Controle Granular por Página */}
      <Dialog open={showPermissoesModal} onOpenChange={setShowPermissoesModal}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              Permissões de Acesso - {selectedUser?.name}
            </DialogTitle>
            <DialogDescription>
              Selecione as páginas individuais que este usuário pode acessar
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 pr-2">
            {selectedUser && (
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-full ${PERFIS[selectedUser.role]?.cor.split(' ')[0] || 'bg-gray-100'}`}>
                    {(() => {
                      const perfil = PERFIS[selectedUser.role];
                      if (perfil) {
                        const Icon = perfil.icon;
                        return <Icon className={`w-5 h-5 ${perfil.cor.split(' ')[1]}`} />;
                      }
                      return <UserCog className="w-5 h-5 text-gray-500" />;
                    })()}
                  </div>
                  <div>
                    <p className="font-medium">{selectedUser.name}</p>
                    <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
                  </div>
                  <Badge className={PERFIS[selectedUser.role]?.cor || ''}>
                    {PERFIS[selectedUser.role]?.nome || selectedUser.role}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={marcarTodas}>
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Todas
                  </Button>
                  <Button variant="outline" size="sm" onClick={desmarcarTodas}>
                    <XCircle className="w-4 h-4 mr-1" />
                    Nenhuma
                  </Button>
                </div>
              </div>
            )}
            
            <div className="text-sm text-muted-foreground text-center">
              {contarPermissoes().ativas} de {contarPermissoes().total} paginas | {contarPermissoes().ativasAcoes} de {contarPermissoes().totalAcoes} acoes
            </div>
            
            <div className="space-y-2">
              {CATEGORIAS.map(categoria => {
                const Icon = categoria.icon;
                const paginas = getPaginasPorCategoria(categoria.id);
                const paginasMarcadas = paginas.filter(p => permissoesEdit.paginas?.[p.id]).length;
                const todasMarcadas = paginasMarcadas === paginas.length;
                const algumasMarcadas = paginasMarcadas > 0 && paginasMarcadas < paginas.length;
                const isExpanded = expandedCategories.includes(categoria.id);
                
                return (
                  <Collapsible 
                    key={categoria.id} 
                    open={isExpanded}
                    onOpenChange={() => toggleExpandCategoria(categoria.id)}
                  >
                    <div className={`border rounded-lg ${categoria.bgCor}`}>
                      <CollapsibleTrigger asChild>
                        <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-black/5 transition-colors">
                          <div className="flex items-center gap-3">
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-muted-foreground" />
                            )}
                            <Icon className={`w-5 h-5 ${categoria.cor}`} />
                            <span className="font-medium">{categoria.nome}</span>
                            <Badge variant="secondary" className="text-xs">
                              {paginasMarcadas}/{paginas.length}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <Checkbox 
                              checked={todasMarcadas}
                              className={algumasMarcadas ? "data-[state=checked]:bg-primary/50" : ""}
                              onCheckedChange={() => toggleCategoria(categoria.id)}
                            />
                            <span className="text-sm text-muted-foreground">
                              {todasMarcadas ? 'Todas' : algumasMarcadas ? 'Parcial' : 'Nenhuma'}
                            </span>
                          </div>
                        </div>
                      </CollapsibleTrigger>
                      
                      <CollapsibleContent>
                        <div className="border-t px-3 pb-3 pt-2 space-y-1 bg-white/50">
                          {paginas.map(pagina => {
                            const acoes = ACOES_POR_PAGINA[pagina.id] || [];
                            const pageOn = permissoesEdit.paginas?.[pagina.id] || false;
                            const cat = pagina.categoria;
                            return (
                              <div key={pagina.id} className="py-2 px-3 rounded hover:bg-muted/50 transition-colors">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-medium">{pagina.label}</span>
                                  <Switch
                                    checked={pageOn}
                                    onCheckedChange={() => togglePagina(pagina.id)}
                                  />
                                </div>
                                {pageOn && acoes.length > 0 && (
                                  <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5 ml-1">
                                    {acoes.map(acao => {
                                      const key = `${cat}.${pagina.id}.${acao}`;
                                      return (
                                        <label key={acao} className="inline-flex items-center gap-1.5 text-xs cursor-pointer select-none">
                                          <Checkbox
                                            checked={permissoesEdit.acoes?.[key] || false}
                                            onCheckedChange={() => toggleAcao(pagina.id, acao)}
                                            className="h-3.5 w-3.5"
                                          />
                                          <span className="text-muted-foreground">{ACOES_LABELS[acao] || acao}</span>
                                        </label>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                );
              })}
            </div>
          </div>

          <DialogFooter className="border-t pt-4 mt-4">
            <Button variant="outline" onClick={() => setShowPermissoesModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSavePermissoes} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Salvar Permissões
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Alterar Senha */}
      <Dialog open={showResetPasswordModal} onOpenChange={setShowResetPasswordModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-orange-600" />
              Alterar Senha
            </DialogTitle>
            <DialogDescription>
              {selectedUser && `Definir nova senha para ${selectedUser.name}`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {selectedUser && (
              <div className="bg-gray-50 p-3 rounded-lg text-sm">
                <p><strong>Usuário:</strong> {selectedUser.name}</p>
                <p><strong>Email:</strong> {selectedUser.email}</p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="customPassword">Nova Senha</Label>
              <Input
                id="customPassword"
                type="text"
                placeholder="Deixe em branco para gerar automaticamente"
                value={resetPasswordData.customPassword}
                onChange={(e) => setResetPasswordData(prev => ({ ...prev, customPassword: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">
                Mínimo 6 caracteres. Se deixar em branco, será gerada uma senha aleatória.
              </p>
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="sendEmail"
                checked={resetPasswordData.sendEmail}
                onCheckedChange={(checked) => setResetPasswordData(prev => ({ ...prev, sendEmail: checked }))}
              />
              <Label htmlFor="sendEmail" className="text-sm font-normal cursor-pointer">
                Enviar senha por email
              </Label>
            </div>
            
            {resetPasswordData.sendEmail && (
              <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-700">
                <Mail className="w-4 h-4 inline mr-2" />
                O email incluirá o link de acesso ao sistema e as credenciais.
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetPasswordModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleResetPassword} 
              disabled={saving || (resetPasswordData.customPassword && resetPasswordData.customPassword.length < 6)}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Alterando...
                </>
              ) : (
                <>
                  <Key className="w-4 h-4 mr-2" />
                  Alterar Senha
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Modal de Perfil */}
      <Dialog open={showPerfilModal} onOpenChange={setShowPerfilModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              {editingPerfil ? 'Editar Perfil' : 'Novo Perfil de Acesso'}
            </DialogTitle>
            <DialogDescription>
              Configure as permissões que este perfil terá acesso
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div className="grid gap-4">
              <div>
                <Label>Nome do Perfil *</Label>
                <Input 
                  value={novoPerfil.nome}
                  onChange={(e) => setNovoPerfil({...novoPerfil, nome: e.target.value})}
                  placeholder="Ex: Coordenador Pedagógico"
                />
              </div>
              <div>
                <Label>Descrição</Label>
                <Input 
                  value={novoPerfil.descricao}
                  onChange={(e) => setNovoPerfil({...novoPerfil, descricao: e.target.value})}
                  placeholder="Ex: Acesso às funções pedagógicas"
                />
              </div>
            </div>
            
            <div>
              <Label className="mb-3 block">Páginas com Acesso</Label>
              <div className="space-y-3 max-h-64 overflow-y-auto border rounded-lg p-4">
                {CATEGORIAS.map((categoria) => (
                  <div key={categoria.id} className="border rounded-lg">
                    <div 
                      className="flex items-center justify-between p-3 bg-gray-50 cursor-pointer"
                      onClick={() => {
                        // Toggle all pages in category
                        const paginasCategoria = Object.entries(TODAS_PAGINAS)
                          .filter(([_, p]) => p.categoria === categoria.id)
                          .map(([key]) => key);
                        
                        const allSelected = paginasCategoria.every(p => novoPerfil.paginas[p]);
                        const newPaginas = {...novoPerfil.paginas};
                        
                        paginasCategoria.forEach(p => {
                          if (allSelected) {
                            delete newPaginas[p];
                          } else {
                            newPaginas[p] = true;
                          }
                        });
                        
                        setNovoPerfil({...novoPerfil, paginas: newPaginas});
                      }}
                    >
                      <span className="font-medium">{categoria.nome}</span>
                      <Badge variant="outline">
                        {Object.entries(TODAS_PAGINAS)
                          .filter(([key, p]) => p.categoria === categoria.id && novoPerfil.paginas[key])
                          .length} / {Object.entries(TODAS_PAGINAS).filter(([_, p]) => p.categoria === categoria.id).length}
                      </Badge>
                    </div>
                    <div className="p-3 grid grid-cols-2 gap-2">
                      {Object.entries(TODAS_PAGINAS)
                        .filter(([_, p]) => p.categoria === categoria.id)
                        .map(([key, pagina]) => (
                          <label key={key} className="flex items-center gap-2 text-sm cursor-pointer">
                            <input
                              type="checkbox"
                              checked={!!novoPerfil.paginas[key]}
                              onChange={(e) => {
                                const newPaginas = {...novoPerfil.paginas};
                                if (e.target.checked) {
                                  newPaginas[key] = true;
                                } else {
                                  delete newPaginas[key];
                                }
                                setNovoPerfil({...novoPerfil, paginas: newPaginas});
                              }}
                              className="rounded"
                            />
                            <span>{pagina.label}</span>
                          </label>
                        ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowPerfilModal(false); setEditingPerfil(null); }}>
              Cancelar
            </Button>
            <Button onClick={handleSavePerfil} disabled={saving || !novoPerfil.nome}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {editingPerfil ? 'Atualizar Perfil' : 'Criar Perfil'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Modal Reset em Lote */}
      <Dialog open={showBulkResetModal} onOpenChange={(open) => {
        setShowBulkResetModal(open);
        if (!open) {
          setBulkPassword('');
          setBulkResetResults(null);
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-orange-600" />
              Alterar Senha em Lote
            </DialogTitle>
            <DialogDescription>
              Alterar a senha de {selectedUsers.length} usuário(s) selecionado(s)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Lista de usuários selecionados */}
            <div className="bg-gray-50 p-3 rounded-lg max-h-32 overflow-y-auto">
              <p className="text-sm font-medium mb-2">Usuários selecionados:</p>
              <div className="flex flex-wrap gap-1">
                {selectedUsers.map(userId => {
                  const user = usuarios.find(u => u.id === userId);
                  return user ? (
                    <Badge key={userId} variant="outline" className="text-xs">
                      {user.name}
                    </Badge>
                  ) : null;
                })}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="bulkPassword">Nova Senha *</Label>
              <Input
                id="bulkPassword"
                type="text"
                placeholder="Digite a nova senha para todos"
                value={bulkPassword}
                onChange={(e) => setBulkPassword(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Mínimo 6 caracteres. Esta senha será aplicada a todos os usuários selecionados.
              </p>
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="bulkSendEmail"
                checked={bulkSendEmail}
                onCheckedChange={(checked) => setBulkSendEmail(checked)}
              />
              <Label htmlFor="bulkSendEmail" className="text-sm font-normal cursor-pointer">
                Enviar nova senha por email para cada usuário
              </Label>
            </div>
            
            {bulkSendEmail && (
              <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-700">
                <Mail className="w-4 h-4 inline mr-2" />
                Cada usuário receberá um email com sua nova senha e link de acesso.
              </div>
            )}
            
            {bulkResetResults && (
              <div className="bg-green-50 p-3 rounded-lg text-sm">
                <p className="font-medium text-green-700 mb-2">Resultado:</p>
                <p className="text-green-600">
                  {bulkResetResults.results.filter(r => r.success).length} senhas alteradas
                </p>
                {bulkSendEmail && (
                  <p className="text-green-600">
                    {bulkResetResults.results.filter(r => r.email_sent).length} emails enviados
                  </p>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkResetModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleBulkResetPassword} 
              disabled={saving || bulkPassword.length < 6}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Alterando...
                </>
              ) : (
                <>
                  <Key className="w-4 h-4 mr-2" />
                  Alterar Senhas
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Editar Perfil do Sistema */}
      <Dialog open={showEditSistemaPerfil} onOpenChange={setShowEditSistemaPerfil}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingSistemaPerfil && PERFIS[editingSistemaPerfil] && (
                <>
                  {(() => {
                    const perfil = PERFIS[editingSistemaPerfil];
                    const Icon = perfil.icon;
                    return (
                      <div className={`p-2 rounded-full ${perfil.cor.split(' ')[0]}`}>
                        <Icon className={`w-5 h-5 ${perfil.cor.split(' ')[1]}`} />
                      </div>
                    );
                  })()}
                  Editar Perfil: {PERFIS[editingSistemaPerfil].nome}
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              Configure quais páginas os usuários com este perfil podem acessar
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 pr-2">
            {CATEGORIAS.map(cat => {
              const paginas = getPaginasPorCategoria(cat.id);
              if (paginas.length === 0) return null;
              
              const todasMarcadas = paginas.every(p => sistemaPerfilPaginas[p.id]);
              const algumaMarcada = paginas.some(p => sistemaPerfilPaginas[p.id]);
              const Icon = cat.icon;
              
              return (
                <div key={cat.id} className={`border rounded-lg ${cat.bgCor}`}>
                  <div 
                    className="flex items-center justify-between p-3 cursor-pointer hover:bg-white/50"
                    onClick={() => toggleExpandCategoria(cat.id)}
                  >
                    <div className="flex items-center gap-2">
                      <Icon className={`w-5 h-5 ${cat.cor}`} />
                      <span className="font-medium">{cat.nome}</span>
                      <Badge variant="outline" className="text-xs">
                        {paginas.filter(p => sistemaPerfilPaginas[p.id]).length}/{paginas.length}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={todasMarcadas}
                        className={algumaMarcada && !todasMarcadas ? 'data-[state=checked]:bg-gray-400' : ''}
                        onCheckedChange={() => toggleSistemaCategoriaPerfil(cat.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <ChevronDown className={`w-4 h-4 transition-transform ${expandedCategories.includes(cat.id) ? 'rotate-180' : ''}`} />
                    </div>
                  </div>
                  
                  {expandedCategories.includes(cat.id) && (
                    <div className="p-3 pt-0 grid grid-cols-2 gap-2">
                      {paginas.map(pagina => (
                        <div 
                          key={pagina.id}
                          className="flex items-center gap-2 p-2 bg-white rounded cursor-pointer hover:bg-gray-50"
                          onClick={() => toggleSistemaPaginaPerfil(pagina.id)}
                        >
                          <Checkbox
                            checked={sistemaPerfilPaginas[pagina.id] || false}
                            onCheckedChange={() => toggleSistemaPaginaPerfil(pagina.id)}
                          />
                          <span className="text-sm">{pagina.label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowEditSistemaPerfil(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveSistemaPerfil} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Salvar Permissões
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Usuarios;
